import { useEffect, useRef, useState } from 'react'
import { parseSalesCsv } from '../lib/csvParser.js'
import { formatMoney } from '../lib/format.js'

// Modal for importing daily sales from a POS CSV export.
//
// Flow: pick file → auto-parse + detect columns → preview rows →
// confirm import. Idempotent: re-uploading the same dates overwrites
// existing rows (upsert on (restaurant_id, sale_date)).
//
// Targets Toast Sales Summary export but works for any POS CSV
// with a date + net/gross sales column. The parser auto-detects
// column names so Square / Clover / Lightspeed should also work.
export default function SalesImportModal({
  open,
  onClose,
  onImport,        // (rows) → Promise<count>
}) {
  const fileRef = useRef(null)
  const [filename, setFilename]   = useState('')
  const [parsed, setParsed]       = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState(null)

  // Reset on open
  useEffect(() => {
    if (open) {
      setFilename('')
      setParsed(null)
      setError(null)
      setSubmitting(false)
      // Clear the file input so picking the same file twice still fires onChange
      if (fileRef.current) fileRef.current.value = ''
    }
  }, [open])

  if (!open) return null

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setFilename(file.name)
    setError(null)
    try {
      const text = await file.text()
      const result = parseSalesCsv(text)
      setParsed(result)
      if (result.rows.length === 0) {
        setError(result.errors[0] ?? 'No usable rows found in this CSV.')
      }
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[SalesImportModal] parse failed', e2)
      setError('Could not read this file. Make sure it\u2019s a CSV export.')
    }
  }

  async function handleConfirm() {
    if (submitting || !parsed?.rows?.length) return
    setSubmitting(true)
    setError(null)
    try {
      await onImport(parsed.rows)
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[SalesImportModal] import failed', e2)
      setError('Could not save to the database. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Quick summary stats for the preview card.
  const previewStats = parsed?.rows?.length
    ? (() => {
        const rows = parsed.rows
        const first = rows[0].sale_date
        const last  = rows[rows.length - 1].sale_date
        const netTotal = rows.reduce((s, r) => s + Number(r.net_sales || 0), 0)
        const grossTotal = rows.reduce((s, r) => s + Number(r.gross_sales || 0), 0)
        return { first, last, netTotal, grossTotal, count: rows.length }
      })()
    : null

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Import sales CSV"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Import sales CSV</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* Instructions */}
          <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
            <div className="mb-1 font-medium">From Toast Web:</div>
            Reports → <span className="font-medium">Sales Summary</span> →
            choose your date range → <span className="font-medium">Export CSV</span>.
            <div className="mt-2 text-xs text-blue-900/70">
              Works for Square, Clover, and Lightspeed exports too — the parser
              auto-detects the date and sales columns.
            </div>
          </div>

          {/* File picker */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="sales-file">
              Choose CSV file
            </label>
            <input
              ref={fileRef}
              id="sales-file"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="block w-full rounded-lg border border-black/15 px-3 py-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700"
            />
            {filename && (
              <p className="mt-1 truncate text-xs text-black/55">
                Selected: <span className="font-medium text-black/80">{filename}</span>
              </p>
            )}
          </div>

          {/* Detected columns + preview */}
          {parsed?.headers?.length > 0 && (
            <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3 text-xs">
              <div className="mb-1 font-medium text-black/60">
                Detected columns
              </div>
              <div className="space-y-0.5">
                <DetectedRow label="Date"           idx={parsed.columns?.dateIdx}  headers={parsed.headers} />
                <DetectedRow label="Net Sales"      idx={parsed.columns?.netIdx}   headers={parsed.headers} />
                <DetectedRow label="Gross Sales"    idx={parsed.columns?.grossIdx} headers={parsed.headers} />
                <DetectedRow label="Transactions"   idx={parsed.columns?.txIdx}    headers={parsed.headers} optional />
              </div>
            </div>
          )}

          {previewStats && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="mb-1 text-xs font-medium text-emerald-900/70">
                Preview
              </div>
              <div className="text-sm text-emerald-900">
                <span className="font-medium tabular-nums">{previewStats.count}</span>{' '}
                {previewStats.count === 1 ? 'day' : 'days'} of sales,{' '}
                <span className="font-medium tabular-nums">{previewStats.first}</span> to{' '}
                <span className="font-medium tabular-nums">{previewStats.last}</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-emerald-900/60">Net total</div>
                  <div className="text-lg font-medium tabular-nums text-emerald-900">
                    {formatMoney(previewStats.netTotal)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-emerald-900/60">Gross total</div>
                  <div className="text-lg font-medium tabular-nums text-emerald-900">
                    {formatMoney(previewStats.grossTotal)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Per-row errors */}
          {parsed?.errors?.length > 0 && parsed.rows.length > 0 && (
            <details className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900">
              <summary className="cursor-pointer font-medium">
                {parsed.errors.length} row{parsed.errors.length === 1 ? '' : 's'} skipped — click to review
              </summary>
              <ul className="mt-2 space-y-1">
                {parsed.errors.slice(0, 20).map((msg, idx) => (
                  <li key={idx} className="leading-snug">{msg}</li>
                ))}
                {parsed.errors.length > 20 && (
                  <li className="text-amber-900/70">…and {parsed.errors.length - 20} more</li>
                )}
              </ul>
            </details>
          )}

          {/* First-pass help when no file picked yet */}
          {!parsed && !error && (
            <p className="text-xs text-black/55">
              Importing the same days again will overwrite existing rows
              (matched on date), so you can re-export and re-upload safely.
            </p>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !parsed?.rows?.length}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Importing\u2026'
              : parsed?.rows?.length
                ? `Import ${parsed.rows.length} ${parsed.rows.length === 1 ? 'day' : 'days'} of sales`
                : 'Pick a CSV first'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DetectedRow({ label, idx, headers, optional }) {
  const found = idx != null && idx >= 0
  return (
    <div className="flex justify-between gap-2">
      <span className="text-black/60">{label}{optional ? ' (optional)' : ''}</span>
      <span className={`truncate font-medium ${found ? 'text-emerald-700' : (optional ? 'text-black/40' : 'text-red-600')}`}>
        {found ? headers[idx] : (optional ? '—' : 'not found')}
      </span>
    </div>
  )
}
